import React, { useCallback } from 'react';

import { Box, IconButton } from '@mui/material';
import { CircleCheck, CircleX } from 'lucide-react';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const NON_ADMIN_TOOLTIP = 'Only admins can respond to upgrade requests';

const useStyles = makeStyles()({
  actionButtonWrapper: {
    display: 'inline-flex',
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
  },
});

interface UpgradeRequestActionsCellProps {
  isAdmin: boolean;
  onReject: (requestId: string) => void;
  onRequestUpdated: () => void;
  requestId: string;
  status: Apollo.UpgradeRequestStatus;
}

export const UpgradeRequestActionsCell = React.memo(function UpgradeRequestActionsCell({
  isAdmin,
  onReject,
  onRequestUpdated,
  requestId,
  status,
}: UpgradeRequestActionsCellProps): JSX.Element | null {
  const { classes } = useStyles();
  const [respondToUpgradeRequest, { loading: responding }] =
    Apollo.useRespondToUpgradeRequestMutation();

  const handleReject = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onReject(requestId);
    },
    [onReject, requestId]
  );

  const handleApprove = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      const { data } = await respondToUpgradeRequest({
        variables: {
          input: {
            approve: true,
            id: requestId,
          },
        },
      });
      if (!data?.respondToUpgradeRequest) return;
      onRequestUpdated();
    },
    [onRequestUpdated, requestId, respondToUpgradeRequest]
  );

  if (status !== Apollo.UpgradeRequestStatus.Pending) return null;

  return (
    <Box className={classes.actions}>
      <AlineTooltip arrow placement="top" title={isAdmin ? 'Reject' : NON_ADMIN_TOOLTIP}>
        <span className={classes.actionButtonWrapper}>
          <IconButton
            aria-label="Reject upgrade request"
            color="error"
            disabled={responding || !isAdmin}
            onClick={handleReject}
          >
            <CircleX size={18} />
          </IconButton>
        </span>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title={isAdmin ? 'Accept' : NON_ADMIN_TOOLTIP}>
        <span className={classes.actionButtonWrapper}>
          <IconButton
            aria-label="Accept upgrade request"
            color="success"
            disabled={responding || !isAdmin}
            onClick={handleApprove}
          >
            <CircleCheck size={18} />
          </IconButton>
        </span>
      </AlineTooltip>
    </Box>
  );
});
